import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { UpcomingUpgradeStatus } from '../franchise-intranet-upgrade-upcoming-status';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiFranchiseIntranetUpgradeListDataModel } from './franchise-intranet-upgrade-list-data.model';
import { FiFranchiseIntranetUpgradeListDataService } from './franchise-intranet-upgrade-list-data.service';
import { FranchiseIntranetUpgradeService } from '../franchise-intranet-upgrade.service';
import { CoreParameterModel } from '@fi-shared/models';
import { FranchiseIntranetUpgradeModel } from '@fi-app/franchise-intranet-upgrade/franchise-intranet-upgrade.model';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-franchise-intranet-upgrade-list',
    templateUrl: './franchise-intranet-upgrade-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-franchise-intranet-upgrade-list' },
    standalone: false,
})
export class FiFranchiseIntranetUpgradeListComponent implements OnInit {
    @Input() upcomingUpgradeStatus: UpcomingUpgradeStatus;

    HasAddAccess: boolean;
    ListData: FiFranchiseIntranetUpgradeListDataModel;
    FiStateNames = FiStateNames;
    EditTabs: EditTabsModel;

    constructor(
        private stateAccessCheckService: StateAccessCheckService,
        private loadingService: LoadingService,
        private statisticService: StatisticService,
        private upgradeListDataService: FiFranchiseIntranetUpgradeListDataService,
        private franchiseIntranetUpgradeService: FranchiseIntranetUpgradeService,
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.setListDataSettings();
        this.statisticService.Visit(ModuleType.CustomFranchiseIntranetUpgrade);
        this.setPageHeaderData();
    }

    private setListDataSettings() {
        this.HasAddAccess =
            this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Upgrade._name) &&
            this.upcomingUpgradeStatus !== UpcomingUpgradeStatus.Both;

        this.ListData = this.upgradeListDataService.getInstance();
        this.ListData.loadDataFunc = this.loadFranchiseIntranetUpgrades.bind(this);
        this.initializeFranchiseIntranetUpgrades();
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(this.appTextService.get('upgrade.upgrades'));
        this.EditTabs.Tabs.push(new EditTabModel('', ''));
    }

    private getSortColumn(): string {
        if (this.ListData.Sort.Column === 'ChangedOnDate') return 'ChangedOn';

        return 'VersionNumber';
    }

    private getFranchiseIntranetUpgradeParameter(): CoreParameterModel {
        const upgradeProcessParameterModel = new CoreParameterModel();
        Object.assign(upgradeProcessParameterModel, this.ListData.getOffsetParams());

        upgradeProcessParameterModel.SelectedSort = {
            ColumnName: this.getSortColumn(),
            SortDirection: this.ListData.Sort.Reverse,
        };
        return upgradeProcessParameterModel;
    }

    private setFranchiseIntranetUpgradesCount() {
        this.franchiseIntranetUpgradeService
            .GetFranchiseIntranetUpgradesCount(this.getFranchiseIntranetUpgradeParameter())
            .then((upgradesCount) => (this.ListData.TotalDataCount = ~~upgradesCount));
    }

    private loadFranchiseIntranetUpgrades(): Promise<void> {
        this.loadingService.startLoading();

        return this.loadingService.trackPromise(
            this.franchiseIntranetUpgradeService
                .GetFranchiseIntranetUpgrades(this.getFranchiseIntranetUpgradeParameter())
                .then((franchiseIntranetUpgrades: FiFranchiseIntranetUpgradeListDataModel[]) => {
                    franchiseIntranetUpgrades.forEach(
                        (franchiseIntranetUpgradeData: FiFranchiseIntranetUpgradeListDataModel) => {
                            const franchiseIntranetUpgrade = new FranchiseIntranetUpgradeModel(
                                this.fiFormatDateService,
                            );
                            franchiseIntranetUpgrade.Initialize(franchiseIntranetUpgradeData, this.fiFormatDateService);
                            this.ListData.Data.push(franchiseIntranetUpgrade);
                        },
                    );
                    this.cdr.detectChanges();
                }),
        );
    }

    private initializeFranchiseIntranetUpgrades() {
        this.setFranchiseIntranetUpgradesCount();
        this.loadFranchiseIntranetUpgrades();
    }
}
